// Which lines a journal holds, and on which day
// (docs/plans/phase3-accounting-and-revenue-share.md §1.1 "Periods", §3, A3).
//
// A closed period never changes. An event dated in a closed period that changed after the
// period was closed posts the difference between its lines now and what closed periods
// already posted, on the first day after the last closed period, marked late.

import type { CalendarDay } from '../time.ts';
import { addDays, compareDays, daysBetween } from '../time.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { AccountKey } from './accounts.ts';
import type { JournalEventKind, JournalLine } from './journal.ts';
import { JOURNAL_EVENT_KINDS, isBalanced, totalsOf } from './journal.ts';

/** One financial event and its lines as they are now. */
export interface SourceEvent {
  readonly kind: JournalEventKind;
  /** The table the event comes from, e.g. `charge_items`. */
  readonly sourceType: string;
  readonly sourceId: string;
  /** A document number (invoice, receipt, GRN, claim); never a patient identifier. */
  readonly reference: string | null;
  readonly eventDay: CalendarDay;
  readonly lines: readonly JournalLine[];
}

export function sourceKeyOf(event: { kind: string; sourceType: string; sourceId: string }) {
  return `${event.kind}|${event.sourceType}|${event.sourceId}`;
}

export interface Posting extends SourceEvent {
  readonly postedOn: CalendarDay;
  /** Dated in a closed period and changed after it closed. */
  readonly late: boolean;
}

function lineKey(l: JournalLine): string {
  return `${l.account}|${l.scopeId ?? ''}`;
}

/** Debit minus credit per account and scope. */
function netByAccount(lines: readonly JournalLine[]) {
  const nets = new Map<string, { sample: JournalLine; net: bigint }>();
  for (const l of lines) {
    const key = lineKey(l);
    const found = nets.get(key);
    const net = (found?.net ?? 0n) + l.debitCents - l.creditCents;
    nets.set(key, { sample: found?.sample ?? l, net });
  }
  return nets;
}

/**
 * The lines that bring what was posted up to what the event says now: per account, the
 * current net less the posted net. Empty when nothing changed. Balanced whenever both inputs
 * are balanced.
 */
export function differenceFromPosted(
  current: readonly JournalLine[],
  posted: readonly JournalLine[],
): JournalLine[] {
  const now = netByAccount(current);
  const before = netByAccount(posted);
  const keys = [...new Set([...now.keys(), ...before.keys()])].sort();
  const out: JournalLine[] = [];
  for (const key of keys) {
    const a = now.get(key);
    const b = before.get(key);
    const delta = (a?.net ?? 0n) - (b?.net ?? 0n);
    if (delta === 0n) continue;
    const sample = a?.sample ?? b?.sample;
    if (sample === undefined) continue;
    out.push({
      account: sample.account,
      scopeId: sample.scopeId,
      counterparty: sample.counterparty,
      debitCents: delta > 0n ? delta : 0n,
      creditCents: delta < 0n ? -delta : 0n,
    });
  }
  return out;
}

export interface PlanInput {
  /** Events dated in the range, and events dated on or before the last closed day. */
  readonly events: readonly SourceEvent[];
  /** What closed periods posted, by `sourceKeyOf`. */
  readonly posted: ReadonlyMap<string, readonly JournalLine[]>;
  readonly from: CalendarDay;
  readonly to: CalendarDay;
  /** The last day of the last closed period, or null when none is closed. */
  readonly lastClosedDay: CalendarDay | null;
  /**
   * The first day of the first closed period: the day the books in Wodi start. Events before
   * it were never posted and stay out (they are in the opening balances). Null when no
   * period is closed.
   */
  readonly booksStartDay: CalendarDay | null;
}

/**
 * The postings of an open range. The range must start after the last closed day (checked by
 * `checkJournalRange`). Late differences land on the first open day, so they appear in a
 * range only when it contains that day.
 */
export function planPostings(input: PlanInput): Posting[] {
  const firstOpenDay = input.lastClosedDay === null ? null : addDays(input.lastClosedDay, 1);
  const lateInRange =
    firstOpenDay !== null &&
    compareDays(firstOpenDay, input.from) >= 0 &&
    compareDays(firstOpenDay, input.to) <= 0;
  const out: Posting[] = [];
  for (const event of input.events) {
    if (input.booksStartDay !== null && compareDays(event.eventDay, input.booksStartDay) < 0)
      continue;
    const closed =
      input.lastClosedDay !== null && compareDays(event.eventDay, input.lastClosedDay) <= 0;
    if (!closed) {
      if (compareDays(event.eventDay, input.from) < 0 || compareDays(event.eventDay, input.to) > 0)
        continue;
      if (event.lines.length === 0) continue;
      out.push({ ...event, postedOn: event.eventDay, late: false });
      continue;
    }
    if (!lateInRange) continue;
    const lines = differenceFromPosted(event.lines, input.posted.get(sourceKeyOf(event)) ?? []);
    if (lines.length === 0) continue;
    out.push({ ...event, lines, postedOn: firstOpenDay, late: true });
  }
  return out.sort(comparePostings);
}

const KIND_ORDER = new Map<string, number>(JOURNAL_EVENT_KINDS.map((k, i) => [k, i]));

function comparePostings(a: Posting, b: Posting): number {
  return (
    compareDays(a.postedOn, b.postedOn) ||
    Number(a.late) - Number(b.late) ||
    (KIND_ORDER.get(a.kind) ?? 0) - (KIND_ORDER.get(b.kind) ?? 0) ||
    (a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0)
  );
}

// --- Summary --------------------------------------------------------------------------------

export interface ResolvedName {
  readonly code: string | null;
  readonly name: string;
}

export type AccountResolver = (account: AccountKey, scopeId: string | null) => ResolvedName;

export interface SummaryLine {
  readonly account: AccountKey;
  readonly scopeId: string | null;
  readonly accountCode: string | null;
  readonly accountName: string;
  readonly counterparty: string | null;
  readonly debitCents: bigint;
  readonly creditCents: bigint;
}

export interface JournalEntry {
  readonly entryNo: number;
  readonly postedOn: CalendarDay;
  readonly kind: JournalEventKind;
  readonly late: boolean;
  readonly eventCount: number;
  readonly lines: readonly SummaryLine[];
}

/**
 * One entry per day, event kind and late flag; within it, one line per account, scope and
 * counterparty, netted to a debit or a credit. What the accountant imports.
 */
export function summariseJournal(
  postings: readonly Posting[],
  resolve: AccountResolver,
): JournalEntry[] {
  const groups = new Map<string, Posting[]>();
  for (const p of [...postings].sort(comparePostings)) {
    const key = `${String(p.postedOn.year)}-${String(p.postedOn.month)}-${String(p.postedOn.day)}|${p.late ? 1 : 0}|${p.kind}`;
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [p]);
    else list.push(p);
  }
  const entries: JournalEntry[] = [];
  for (const list of groups.values()) {
    const first = list[0];
    if (first === undefined) continue;
    const nets = new Map<string, { line: JournalLine; net: bigint }>();
    for (const p of list)
      for (const l of p.lines) {
        const key = `${l.account}|${l.scopeId ?? ''}|${l.counterparty ?? ''}`;
        const found = nets.get(key);
        nets.set(key, {
          line: found?.line ?? l,
          net: (found?.net ?? 0n) + l.debitCents - l.creditCents,
        });
      }
    const lines: SummaryLine[] = [...nets.values()]
      .filter((n) => n.net !== 0n)
      .map((n) => {
        const name = resolve(n.line.account, n.line.scopeId);
        return {
          account: n.line.account,
          scopeId: n.line.scopeId,
          accountCode: name.code,
          accountName: name.name,
          counterparty: n.line.counterparty,
          debitCents: n.net > 0n ? n.net : 0n,
          creditCents: n.net < 0n ? -n.net : 0n,
        };
      })
      .sort(
        (a, b) =>
          Number(b.debitCents > 0n) - Number(a.debitCents > 0n) ||
          a.account.localeCompare(b.account),
      );
    if (lines.length === 0) continue;
    entries.push({
      entryNo: entries.length + 1,
      postedOn: first.postedOn,
      kind: first.kind,
      late: first.late,
      eventCount: list.length,
      lines,
    });
  }
  return entries;
}

export interface TrialBalanceRow {
  readonly account: AccountKey;
  readonly scopeId: string | null;
  readonly accountCode: string | null;
  readonly accountName: string;
  readonly debitCents: bigint;
  readonly creditCents: bigint;
}

/** Debits and credits per account across the journal, gross (not netted). */
export function trialBalance(entries: readonly JournalEntry[]): TrialBalanceRow[] {
  const rows = new Map<string, TrialBalanceRow>();
  for (const e of entries)
    for (const l of e.lines) {
      const key = `${l.account}|${l.scopeId ?? ''}`;
      const found = rows.get(key);
      rows.set(key, {
        account: l.account,
        scopeId: l.scopeId,
        accountCode: l.accountCode,
        accountName: l.accountName,
        debitCents: (found?.debitCents ?? 0n) + l.debitCents,
        creditCents: (found?.creditCents ?? 0n) + l.creditCents,
      });
    }
  return [...rows.values()].sort(
    (a, b) => a.account.localeCompare(b.account) || a.accountName.localeCompare(b.accountName),
  );
}

export function journalTotals(entries: readonly JournalEntry[]) {
  const all = entries.flatMap((e) => e.lines);
  const totals = totalsOf(all);
  return {
    debitCents: totals.debit,
    creditCents: totals.credit,
    balanced: totals.debit === totals.credit && entries.every((e) => isBalanced(e.lines)),
    entryCount: entries.length,
    lineCount: all.length,
  };
}

// --- Reconciliation with Wodi's reports -----------------------------------------------------

export interface JournalReconciliationFigures {
  /** Revenue credited per category, from charges dated in the range (not late lines). */
  readonly revenueByCategory: ReadonlyMap<string, bigint>;
  readonly waivers: bigint;
  readonly discounts: bigint;
  readonly creditNotes: bigint;
  /** Money debited by payments, per money account. */
  readonly receivedByAccount: ReadonlyMap<string, bigint>;
  /** Money credited by refunds, per money account. */
  readonly refundedByAccount: ReadonlyMap<string, bigint>;
}

function add(map: Map<string, bigint>, key: string, cents: bigint) {
  map.set(key, (map.get(key) ?? 0n) + cents);
}

/**
 * The figures the revenue and daily collections reports show for the same range, read back
 * from the journal. Late lines are left out: the reports count events by their own dates.
 */
export function reconciliationFigures(postings: readonly Posting[]): JournalReconciliationFigures {
  const revenue = new Map<string, bigint>();
  const received = new Map<string, bigint>();
  const refunded = new Map<string, bigint>();
  let waivers = 0n;
  let discounts = 0n;
  let creditNotes = 0n;
  for (const p of postings) {
    if (p.late) continue;
    for (const l of p.lines) {
      if (p.kind === 'charge' && l.account.startsWith('revenue.'))
        add(revenue, l.account.slice('revenue.'.length), l.creditCents - l.debitCents);
      if (p.kind === 'waiver' && l.account === 'contra.waivers')
        waivers += l.debitCents - l.creditCents;
      if (p.kind === 'discount' && l.account === 'contra.discounts')
        discounts += l.debitCents - l.creditCents;
      if (p.kind === 'credit_note' && l.account === 'contra.credit_notes')
        creditNotes += l.debitCents - l.creditCents;
      if (p.kind === 'payment' && l.account.startsWith('money.'))
        add(received, l.account, l.debitCents - l.creditCents);
      if (p.kind === 'refund' && l.account.startsWith('money.'))
        add(refunded, l.account, l.creditCents - l.debitCents);
    }
  }
  return {
    revenueByCategory: revenue,
    waivers,
    discounts,
    creditNotes,
    receivedByAccount: received,
    refundedByAccount: refunded,
  };
}

// --- Ranges and periods ---------------------------------------------------------------------

/** A journal covers at most about a quarter: the queries stay on indexed, bounded ranges. */
export const MAX_JOURNAL_DAYS = 92;

export type JournalRangeErrorCode =
  'journal.range_reversed' | 'journal.range_too_long' | 'journal.range_in_closed_period';

export const JOURNAL_RANGE_ERRORS: Record<JournalRangeErrorCode, string> = {
  'journal.range_reversed': 'The start date is after the end date.',
  'journal.range_too_long': `A journal covers at most ${String(MAX_JOURNAL_DAYS)} days. Choose a shorter range.`,
  'journal.range_in_closed_period':
    'Part of that range is in a closed period. Open the closed period to see or download its journal, and start the range after it.',
};

export function checkJournalRange(
  from: CalendarDay,
  to: CalendarDay,
  lastClosedDay: CalendarDay | null,
): Result<null, JournalRangeErrorCode> {
  if (compareDays(from, to) > 0) return err('journal.range_reversed');
  if (daysBetween(from, to) + 1 > MAX_JOURNAL_DAYS) return err('journal.range_too_long');
  if (lastClosedDay !== null && compareDays(from, lastClosedDay) <= 0)
    return err('journal.range_in_closed_period');
  return ok(null);
}

export type PeriodCloseErrorCode =
  'period.not_contiguous' | 'period.reversed' | 'period.not_ended' | 'period.too_long';

export const PERIOD_CLOSE_ERRORS: Record<PeriodCloseErrorCode, string> = {
  'period.not_contiguous':
    'A period starts the day after the last closed period ends. Choose the next period.',
  'period.reversed': 'The period ends before it starts.',
  'period.not_ended': 'A period can be closed only after its last day has passed.',
  'period.too_long': `A period covers at most ${String(MAX_JOURNAL_DAYS)} days.`,
};

/**
 * Whether a period may be closed: it starts the day after the last closed period (any day
 * for the first), ends after it starts, has ended (its last day is before today) and is at
 * most `MAX_JOURNAL_DAYS` long.
 */
export function checkPeriodClose(input: {
  startsOn: CalendarDay;
  endsOn: CalendarDay;
  today: CalendarDay;
  lastClosedDay: CalendarDay | null;
}): Result<null, PeriodCloseErrorCode> {
  if (
    input.lastClosedDay !== null &&
    compareDays(input.startsOn, addDays(input.lastClosedDay, 1)) !== 0
  )
    return err('period.not_contiguous');
  if (compareDays(input.startsOn, input.endsOn) > 0) return err('period.reversed');
  if (compareDays(input.endsOn, input.today) >= 0) return err('period.not_ended');
  if (daysBetween(input.startsOn, input.endsOn) + 1 > MAX_JOURNAL_DAYS)
    return err('period.too_long');
  return ok(null);
}
